import React, { useEffect, useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
} from '@mui/material';
import {
  People as PeopleIcon,
  Restaurant as RestaurantIcon,
  CalendarMonth as CalendarIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { AuthContext } from '../../context/AuthContext';
import { useIsMobile } from '../../hooks';
import { MobileList } from '../../components/common';
import api from '../../services/api';
import { colors, grey, primary, success, warning, info } from '../../styles/tokens';

/**
 * Dashboard Coach
 */
const CoachDashboard = () => {
  const { user } = useContext(AuthContext);
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [weekPlans, setWeekPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [clientData, recipeData, weekPlanData] = await Promise.all([
        api.get('/coach/clients'),
        api.get('/coach/recipes'),
        api.get('/coach/weekplans'),
      ]);

      setClients(clientData.clients || clientData || []);
      setRecipes(recipeData.recipes || recipeData || []);
      setWeekPlans(weekPlanData.plans || weekPlanData || []);
    } catch (err) {
      console.error('Erreur CoachDashboard:', err.message);
      setError('Impossible de charger les donnees du dashboard.');
    } finally {
      setLoading(false);
    }
  };

  // StatCard component - responsive pour mobile
  const StatCard = ({ title, value, icon: Icon, bgColor, iconColor, subtitle }) => (
    <Card>
      <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: isMobile ? '0.625rem' : '0.6875rem',
                fontWeight: 600,
                color: grey[500],
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 0.5,
                whiteSpace: isMobile ? 'normal' : 'nowrap',
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>
            <Typography sx={{ fontSize: isMobile ? '1.25rem' : '1.5rem', fontWeight: 700, color: grey[800] }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: '0.75rem', color: grey[500], mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              width: isMobile ? 32 : 40,
              height: isMobile ? 32 : 40,
              bgcolor: bgColor,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: isMobile ? 16 : 20, color: iconColor }} />
          </Avatar>
        </Stack>
      </CardContent>
    </Card>
  );

  // Chip styles
  const getStatusChipProps = (active) => {
    return active
      ? { label: 'Actif', sx: { bgcolor: success[50], color: success.dark } }
      : { label: 'Inactif', sx: { bgcolor: grey[100], color: grey[600] } };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const activeClients = clients.filter(client => client.active);
  const stats = {
    totalClients: clients.length,
    activeClients: activeClients.length,
    totalRecipes: recipes.length,
    totalPlans: weekPlans.length,
  };

  return (
    <>
      <Helmet>
        <title>Dashboard - Coach Yoco</title>
      </Helmet>

      <Box>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h1">Dashboard</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Bienvenue, {user?.firstName}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Stats - 2 colonnes sur mobile, 4 sur desktop */}
        <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mb: 3 }}>
          <Grid item xs={6} lg={3}>
            <StatCard
              title="Total Clients"
              value={stats.totalClients}
              icon={PeopleIcon}
              bgColor={primary[50]}
              iconColor={primary[500]}
            />
          </Grid>
          <Grid item xs={6} lg={3}>
            <StatCard
              title="Clients Actifs"
              value={stats.activeClients}
              icon={PeopleIcon}
              bgColor={success[50]}
              iconColor={success.main}
            />
          </Grid>
          <Grid item xs={6} lg={3}>
            <StatCard
              title="Recettes"
              value={stats.totalRecipes}
              icon={RestaurantIcon}
              bgColor={warning[50]}
              iconColor={warning.main}
            />
          </Grid>
          <Grid item xs={6} lg={3}>
            <StatCard
              title="Programmes"
              value={stats.totalPlans}
              icon={CalendarIcon}
              bgColor={info[50]}
              iconColor={info.main}
            />
          </Grid>
        </Grid>

        {/* Active Clients */}
        <Card sx={{ mb: 3 }} className={isMobile ? 'mobile-list-container-with-header' : ''}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${colors.border}` }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h3">Clients actifs</Typography>
              <Button
                component={Link}
                to="/coach/clients"
                size="small"
                endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />}
              >
                Voir tous
              </Button>
            </Stack>
          </Box>

          {activeClients.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2">
                Aucun client actif pour le moment.
              </Typography>
            </Box>
          ) : isMobile ? (
            /* Mobile: Liste en cards */
            <Box sx={{ px: 2, pt: 0, pb: 1 }}>
              <MobileList
                items={activeClients.slice(0, 5)}
                renderItem={(client) => {
                  const statusProps = getStatusChipProps(client.active);
                  return {
                    primary: `${client.firstName} ${client.lastName}`,
                    secondary: client.email,
                    chips: [{ label: statusProps.label, sx: statusProps.sx }],
                    onClick: () => navigate(`/coach/clients/view/${client._id}`),
                  };
                }}
                emptyMessage="Aucun client actif"
              />
            </Box>
          ) : (
            /* Desktop: Table classique */
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Inscription</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeClients.slice(0, 5).map((client) => {
                    const statusProps = getStatusChipProps(client.active);
                    return (
                      <TableRow
                        key={client._id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/coach/clients/view/${client._id}`)}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {client.firstName} {client.lastName}
                            </Typography>
                            <Typography variant="caption">
                              {client.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusProps.label}
                            size="small"
                            sx={statusProps.sx}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">
                            {new Date(client.createdAt).toLocaleDateString('fr-FR')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        {/* Quick Actions */}
        <Grid container spacing={isMobile ? 1.5 : 2}>
          <Grid item xs={6} md={4}>
            <Card
              component={Link}
              to="/coach/clients/create"
              sx={{
                textDecoration: 'none',
                display: 'block',
                '&:hover': {
                  borderColor: colors.borderStrong,
                },
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: primary[50] }}>
                    <AddIcon sx={{ fontSize: 18, color: primary[500] }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h4">Ajouter Client</Typography>
                    <Typography variant="caption">Nouveau client</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={4}>
            <Card
              component={Link}
              to="/coach/recipes/create"
              sx={{
                textDecoration: 'none',
                display: 'block',
                '&:hover': {
                  borderColor: colors.borderStrong,
                },
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: warning[50] }}>
                    <RestaurantIcon sx={{ fontSize: 18, color: warning.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h4">Ajouter Recette</Typography>
                    <Typography variant="caption">Nouvelle recette</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card
              component={Link}
              to="/coach/plans/create"
              sx={{
                textDecoration: 'none',
                display: 'block',
                '&:hover': {
                  borderColor: colors.borderStrong,
                },
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: info[50] }}>
                    <CalendarIcon sx={{ fontSize: 18, color: info.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h4">Ajouter Programme</Typography>
                    <Typography variant="caption">Nouveau programme</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default CoachDashboard;
